@if (session.customer(); as c) {
  <div class="mx-auto flex max-w-[1400px] flex-col gap-6">

    <!-- HERO CARD -->
    <div class="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <p class="flex flex-wrap items-center gap-2 cmt-eyebrow">
            Customer · {{ c.id }}
            <hlm-badge [variant]="statusBadgeTone()">
              <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
              {{ statusLabel() }}
            </hlm-badge>
            @if (!editMode() && c.testUser) {
              <hlm-badge variant="accent">Test User</hlm-badge>
            }
            @if (!editMode() && c.billingEnabled === false) {
              <hlm-badge variant="purple" hlmTooltip="Billing disabled">Friendly user</hlm-badge>
            }
          </p>
          <h1 class="mt-2 font-display text-2xl font-semibold leading-tight text-foreground">{{ c.companyName || '—' }}</h1>
          <p class="text-sm text-muted-foreground">{{ session.brandName() }}</p>
        </div>
      </div>
    </div>

    <!-- Sub-hero actions: portal/tool launches (right-aligned), mirrors the User Account page. -->
    @if (!editMode()) {
      <div class="flex flex-wrap items-center justify-end gap-2">
        <button hlmBtn variant="outline" (click)="openCallViewer()">
          <hlm-icon name="lucidePhone" size="0.9rem" />
          Open Call Viewer
        </button>
        <button hlmBtn variant="outline" (click)="openPrintLabel()">
          <hlm-icon name="lucidePrinter" size="0.9rem" />
          Print Address Label
        </button>
      </div>
    }

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-stretch">
      <!-- Company section -->
      <div class="flex flex-col gap-3">
        <section class="flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-sm">
          <header class="mb-4 flex items-center gap-2.5">
            <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <hlm-icon name="lucideBuilding2" size="1rem" />
            </span>
            <h2 class="font-display text-lg font-medium text-foreground">Company &amp; Status</h2>
          </header>

          <dl class="flex flex-col divide-y divide-border/70">
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">
                Company Name
              </dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <input hlmInput size="sm" class="text-left" [formControl]="form.controls.companyName" />
                } @else {
                  <span class="text-sm font-medium text-foreground">{{ c.companyName || '—' }}</span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Creator</dt>
              <dd class="text-sm font-medium text-foreground">{{ c.contractCreator ? (c.contractCreator.firstName + ' ' + c.contractCreator.lastName).trim() : '—' }}</dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow flex items-center gap-1.5">
                Test User
                <button
                  type="button"
                  class="relative inline-flex cursor-help items-center rounded-sm after:absolute after:-inset-3.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:[outline-color:var(--ring)]"
                  aria-label="Help: Test User"
                  hlmTooltip="'Yes' = test account: excluded from BI statistics and call-center prognosis. No billing effect.">
                  <hlm-icon name="lucideCircleHelp" size="1rem" aria-hidden="true" />
                </button>
              </dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <div class="ml-auto max-w-[200px]">
                    <hlm-select
                      [options]="yesNoOptions"
                      [value]="form.controls.testUser.value"
                      (valueChange)="form.controls.testUser.setValue($any($event))"
                    />
                  </div>
                } @else {
                  <span class="text-sm font-medium text-foreground">{{ c.testUser ? 'Yes' : 'No' }}</span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Creation Date</dt>
              <dd class="font-mono-feat text-sm text-foreground">{{ c.creationDate | date:'dd.MM.yyyy' }}</dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Start Date</dt>
              <dd class="font-mono-feat text-sm text-foreground">{{ c.startDate | date:'dd.MM.yyyy' }}</dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">End Date</dt>
              <dd [class]="'font-mono-feat text-sm ' + (c.endDate ? 'text-foreground' : 'text-muted-foreground/60')">
                {{ c.endDate ? (c.endDate | date:'dd.MM.yyyy') : '—' }}
              </dd>
            </div>
          </dl>
        </section>
      </div>

      <!-- Details section -->
      <div class="flex flex-col gap-3">
        <section class="flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-sm">
          <header class="mb-4 flex items-center gap-2.5">
            <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <hlm-icon name="lucideReceipt" size="1rem" />
            </span>
            <h2 class="font-display text-lg font-medium text-foreground">Settings</h2>
          </header>

          <dl class="flex flex-col divide-y divide-border/70">
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow flex items-center gap-1.5">
                Billing Enabled
                <button
                  type="button"
                  class="relative inline-flex cursor-help items-center rounded-sm after:absolute after:-inset-3.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:[outline-color:var(--ring)]"
                  aria-label="Help: Billing Enabled"
                  hlmTooltip="'No' = Friendly user: the customer is permanently excluded from billing and some BI queries. Re-enabling generates all open invoices at the next billing run.">
                  <hlm-icon name="lucideCircleHelp" size="1rem" aria-hidden="true" />
                </button>
              </dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode() && canEditBilling) {
                  <div class="ml-auto max-w-[200px]">
                    <hlm-select
                      [options]="yesNoOptions"
                      [value]="form.controls.billingEnabled.value"
                      (valueChange)="form.controls.billingEnabled.setValue($any($event))"
                    />
                  </div>
                } @else {
                  <span class="text-sm font-medium text-foreground">{{ (c.billingEnabled ?? true) ? 'Yes' : 'No' }}</span>
                }
                @if (editMode() && !canEditBilling) {
                  <div class="mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-left">
                    <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
                    <p class="text-sm text-foreground">
                      Changing a customer's billing status is a high-impact action. It requires the
                      <em>billing-status</em> permission — ask a team lead.
                    </p>
                  </div>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Privacy Level</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <div class="ml-auto max-w-[200px]">
                    <hlm-select
                      [options]="privacyOptions"
                      [value]="form.controls.privacyLevel.value"
                      (valueChange)="form.controls.privacyLevel.setValue($any($event) || 'STANDARD')"
                    />
                  </div>
                } @else {
                  <span class="inline-flex items-center gap-2">
                    <span class="text-sm font-medium text-foreground">{{ c.privacyLevel }}</span>
                    @if (c.privacyLevel === 'HIGH') {
                      <hlm-badge variant="success">HIGH</hlm-badge>
                    }
                  </span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Routing Priority</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <input
                    hlmInput size="sm"
                    class="ml-auto max-w-[160px] text-right"
                    type="number" step="0.01"
                    [formControl]="form.controls.routingPriority"
                  />
                  <p class="mt-1 text-sm text-muted-foreground">0 = Normal, 0.10 = Reduced</p>
                } @else {
                  <span class="text-sm font-medium text-foreground">{{ routingPriorityText() }}</span>
                  <span class="ml-1 font-mono-feat text-sm text-muted-foreground">({{ c.routingPriority }})</span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Max Days History</dt>
              <dd class="min-w-0 flex-1 text-right">
                @if (editMode()) {
                  <input
                    hlmInput size="sm"
                    class="ml-auto max-w-[160px] text-right"
                    type="number"
                    [formControl]="form.controls.maxDaysHistory"
                  />
                  <p class="mt-1 text-sm text-muted-foreground">days, -1 = endless</p>
                } @else {
                  <span class="text-sm font-medium text-foreground">
                    {{ c.maxDaysHistory === -1 ? 'Endless' : c.maxDaysHistory }}
                  </span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Brand</dt>
              <dd class="text-sm text-foreground">{{ session.brandName() }} <span class="font-mono-feat text-muted-foreground">({{ c.brandId }})</span></dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">Sub Brand</dt>
              <dd class="font-mono-feat text-sm">
                @if (c.subBrandId >= 1) {
                  <span class="text-foreground">{{ c.subBrandId }} — {{ c.subBrandName }}</span>
                } @else {
                  <span class="text-muted-foreground/60">—</span>
                }
              </dd>
            </div>

            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="cmt-eyebrow">External ID</dt>
              <dd [class]="'font-mono-feat text-sm ' + (c.externalId ? 'text-foreground' : 'text-muted-foreground/60')">
                {{ c.externalId || '—' }}
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </div>

    <!-- Actions — items-start so the Delete inline explainer (when disabled) doesn't
         offset the Edit button vertically (CLAUDE.md → Inline action explainer). -->
    <div class="flex flex-wrap items-start gap-2">
      @if (!editMode()) {
        <button hlmBtn variant="default" (click)="startEdit()" [disabled]="editDisabled()">
          <hlm-icon name="lucidePencil" size="0.9rem" />
          Edit
        </button>
        <!-- Delete is blocked until the customer's contract has ended; show an inline
             explainer instead of a tooltip because disabled buttons swallow hover. -->
        <div class="flex flex-col items-start gap-1">
          <button
            hlmBtn
            variant="destructive"
            (click)="openDeleteCustomer()"
            [disabled]="deleteDisabled() || deleteInProgress()"
          >
            <hlm-icon name="lucideTrash2" size="0.9rem" />
            Delete Customer Data
          </button>
          @if (deleteDisabled()) {
            <p class="max-w-[18rem] text-sm leading-snug text-foreground">{{ deleteDisabledReason() }}.</p>
          }
        </div>
      } @else {
        <button hlmBtn variant="default" (click)="save()" [disabled]="form.invalid || saving()">
          @if (saving()) {
            <hlm-spinner size="16" />
            Saving…
          } @else {
            <hlm-icon name="lucideSave" size="0.9rem" />
            Save
          }
        </button>
        <button hlmBtn variant="outline" (click)="cancelEdit()">Cancel</button>
      }
    </div>
  </div>
}
